import Reveal from "@/components/Reveal";
import StatusPill from "@/components/StatusPill";
import FlowDiagram from "@/components/FlowDiagram";

/**
 * Full section block for the Impact page. Renders an anchored section
 * per loop with number, status, name, long description, and flow diagram.
 *
 * @param {Object} props
 * @param {import("@/data/loops").CircularLoop} props.loop
 * @param {number} props.index           Used to alternate background colour
 * @param {React.ReactNode} [props.visual] Optional supporting visual (e.g. CircularLoop SVG)
 */
const LoopDetailSection = ({ loop, index, visual }) => {
  const alt = index % 2 === 1;
  const bg = alt ? "bg-ecospora-white" : "bg-ecospora-cream";

  return (
    <section
      id={loop.id}
      data-testid={`loop-detail-${loop.id}`}
      className={`scroll-mt-24 ${bg}`}
    >
      <div className="container-x section-y">
        <Reveal>
          <div className="grid md:grid-cols-12 gap-10 md:gap-12 items-start">
            <div className={`${visual ? "md:col-span-6" : "md:col-span-12"} max-w-3xl`}>
              <div className="flex items-center justify-between gap-4 mb-6 flex-wrap">
                <span className="font-display text-5xl md:text-6xl font-bold text-ecospora-ochre tracking-tight leading-none">
                  {loop.number}
                </span>
                <StatusPill
                  status={loop.status}
                  label={
                    loop.status === "running"
                      ? "Running today"
                      : `Building with ${loop.partner}`
                  }
                />
              </div>

              <h3 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-ecospora-green-deep leading-[1.1] mb-6">
                {loop.name}
              </h3>

              <p className="text-lg text-ecospora-charcoal/80 leading-[1.7] mb-10">
                {loop.longDescription}
              </p>

              <div className="p-6 md:p-7 rounded-2xl bg-ecospora-cream border border-ecospora-green-deep/15">
                <span className="text-[11px] font-semibold tracking-[0.22em] uppercase text-ecospora-green-mid">
                  How the loop turns
                </span>
                <div className="mt-4">
                  <FlowDiagram steps={loop.flow} variant="full" />
                </div>
              </div>
            </div>

            {visual && <div className="md:col-span-6">{visual}</div>}
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export default LoopDetailSection;
